<script setup>
    import { computed } from 'vue';
    import TextInput from '@/components/TextInput.vue';
    import SelectInput from '@/components/SelectInput.vue';
    import SelectMultiInput from '@/components/SelectMultiInput.vue';
    import TextAreaInput from '@/components/TextAreaInput.vue';
    import MoneyInput from '@/components/MoneyInput.vue';
    import PercentInput from '@/components/PercentInput.vue';
    import InputWeight from '@/components/InputWeight.vue';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    const props = defineProps({
        modelValue: { required: true },
        label: { required: true },
        type: { required: true },
        required: { required: false, default: false },
        options: { required: false, default: [] },
        description: { required: false, default: '' },
        example: { required: false, default: '' },
        errors: { required: false, default: [] },
        disabled: { required: false, default: false },
    });
    
    const emit = defineEmits(['update:modelValue', 'onChange']);
    
    const value = computed({
        get: () => props.modelValue,
        set: (value) => {
            emit('update:modelValue', value);
            emit('onChange', value);
        },
    });
</script>

<template>
    <text-input
        v-if="type === 'text'"
        type="text"
        :label="label"
        v-model="value"
        :required="required"
        :sub-info="description"
        :errors="errors"
        :disabled="disabled"
    />

    <text-input
        v-if="type === 'number'"
        type="number"
        :label="label"
        v-model="value"
        :required="required"
        :sub-info="description"
        :errors="errors"
        :disabled="disabled"
    />

    <text-input
        v-if="type === 'date'"
        type="date"
        :label="label"
        v-model="value"
        :required="required"
        :sub-info="description"
        :errors="errors"
        :disabled="disabled"
    />

    <text-input
        v-if="type === 'color'"
        type="color"
        :label="label"
        v-model="value"
        :required="required"
        :sub-info="description"
        :errors="errors"
        :disabled="disabled"
    />

    <text-input
        v-if="type === 'email'"
        type="email"
        :label="label"
        v-model="value"
        :required="required"
        :sub-info="description"
        :errors="errors"
        :disabled="disabled"
    />

    <money-input
        v-if="type === 'currency'"
        :label="label"
        v-model="value"
        :required="required"
        :sub-info="description"
        :errors="errors"
        :disabled="disabled"
    />

    <percent-input
        v-if="type === 'percent'"
        :label="label"
        v-model="value"
        :required="required"
        :sub-info="description"
        :errors="errors"
        :disabled="disabled"
        suffix="%"
    />

    <text-area-input
        v-if="type === 'textarea'"
        :label="label"
        v-model="value"
        :required="required"
        :sub-info="description"
        :errors="errors"
        :disabled="disabled"
    />

    <text-input
        v-if="type === 'phone'"
        :label='label'
        v-model='value'
        v-maska='["(##) #####-####", "(##) ####-####"]'
        placeholder='(__) ____-____'
        :required='required'
        :sub-info="description"
        :errors="errors"
        :disabled="disabled"
    />

    <div class='border border-dashed rounded mb-3' v-if="type === 'boolean'">
        <div class='d-flex mt-2 ms-2 p-1 align-items-center justify-content-between'>
            <label class='me-3' v-if='label'>
                {{ label }}
                <span v-if='required' class='text-danger'>*</span><br>
                <small v-if='description' class='text-info d-block' style="margin-top: -5px">{{ description }}</small>
            </label>
            <label class='switch s-icons s-outline s-outline-primary mt-1'>
                <input type='checkbox' v-model='value' :disabled="disabled" />
                <span class='slider round'></span>
            </label>
        </div>
    </div>

    <select-input
        v-if="type === 'select'"
        :label="label"
        :options="options.map(option => ({ name: option, id: option }))"
        v-model="value"
        :required="required"
        :sub-info="description"
        :errors="errors"
        :disabled="disabled"
    />

    <select-multi-input
        v-if="type === 'multiselect'"
        :label="label"
        :options="options.map(option => ({ name: option, id: option }))"
        v-model="value"
        :required="required"
        :sub-info="description"
        :errors="errors"
        :disabled="disabled"
    />
    
    <input-weight
        v-if="type === 'peso'"
        :label="label"
        :options="options"
        v-model="value"
        :required="required"
        :sub-info="description"
        :errors="errors"
        :disabled="disabled"
    />
</template>

<style scoped>
    
</style>
